/**
 * useReadArticles
 *
 * Article "read" tracking. Backed by the `/me/article-interactions` API
 * (each row's `readAt` field). `markAsRead` is idempotent — re-reading
 * an already-read article does NOT overwrite the original timestamp.
 */
import React, { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  type ArticleInteraction,
  articleInteractionsKey,
  useArticleInteractions,
  useUpsertArticleInteraction,
} from "./useArticleInteractions";

/**
 * Pass-through provider kept for backwards compatibility (Olly's App.tsx
 * still mounts it). No state — TanStack Query is the source of truth.
 */
export function ReadArticlesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return <>{children}</>;
}

export function useReadArticles() {
  const { data: interactions = [], isLoading } = useArticleInteractions();
  const { mutate } = useUpsertArticleInteraction();
  const qc = useQueryClient();

  const readArticles = useMemo(
    () => interactions.filter((i) => i.readAt != null).map((i) => i.articleId),
    [interactions],
  );

  const readCount = readArticles.length;

  const isRead = useCallback(
    (articleId: string) =>
      interactions.some((i) => i.articleId === articleId && i.readAt != null),
    [interactions],
  );

  // Read the latest cache at call-time so re-opening an article quickly
  // doesn't fire a second PUT — onMutate has already set readAt
  // optimistically before this runs.
  const markAsRead = useCallback(
    (articleId: string) => {
      const current =
        qc.getQueryData<ArticleInteraction[]>(articleInteractionsKey) ?? [];
      const existing = current.find((i) => i.articleId === articleId);
      if (existing?.readAt != null) return; // already marked — preserve original timestamp
      mutate({
        articleId,
        body: { readAt: new Date().toISOString() },
      });
    },
    [qc, mutate],
  );

  return { readArticles, readCount, isRead, markAsRead, isLoading };
}
